/**
 * Quiz statistics page.
 * Shows aggregate performance, participation trends, and per-question accuracy for a collection.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spin,
  Tag,
  message,
  Empty,
  Progress,
  Image,
} from 'antd';
import {
  ArrowLeftOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  ReloadOutlined,
  EditOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import PagesWrapper from '../../PagesWrapper';
import QuestionAnalyticsModal from './QuestionAnalyticsModal';
import quizCollectionsService from '../../services/quizCollectionsService';
import { getFileUrl } from '../../utils/urlUtils';
import { formatDurationFromSeconds } from '../../utils/timeUtils';
import styles from './QuizStatistics.module.css';

const QUESTION_IMAGE_SIZE = 96;
const CHOICE_IMAGE_SIZE = 36;

/**
 * Analytics dashboard for a single quiz collection.
 *
 * @returns {JSX.Element|null} Statistics page content or null while data is unavailable.
 */
const QuizStatistics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analyticsQuestion, setAnalyticsQuestion] = useState(null);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await quizCollectionsService.getQuizStatistics(id);
      setStatistics(data);
    } catch (error) {
      message.error(t('quiz.statistics.fetchError', 'Failed to load statistics'));
      navigate('/quiz-collections');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  /**
   * Render the headline KPI cards shown at the top of the dashboard.
   *
   * @returns {JSX.Element} Summary cards section.
   */
  const renderSummaryCards = () => (
    <div className={styles.summaryCards}>
      <div className={styles.summaryCard}>
        <TeamOutlined className={styles.cardIcon} />
        <div className={styles.value}>{statistics.totalAttempts}</div>
        <div className={styles.label}>{t('quiz.statistics.totalAttempts', 'Total Attempts')}</div>
      </div>
      <div className={styles.summaryCard}>
        <QuestionCircleOutlined className={styles.cardIcon} />
        <div className={styles.value}>{statistics.anonymousAttempts}</div>
        <div className={styles.label}>{t('quiz.statistics.anonymous', 'Anonymous Users')}</div>
      </div>
      <div className={styles.summaryCard}>
        <UserOutlined className={styles.cardIcon} />
        <div className={styles.value}>{statistics.authenticatedAttempts}</div>
        <div className={styles.label}>{t('quiz.statistics.authenticated', 'Registered Users')}</div>
      </div>
      <div className={styles.summaryCard}>
        <TrophyOutlined className={styles.cardIcon} />
        <div className={styles.value}>
          {statistics.averageScorePercent != null
            ? `${statistics.averageScorePercent.toFixed(1)}%`
            : '-'}
        </div>
        <div className={styles.label}>{t('quiz.statistics.avgScore', 'Avg. Score')}</div>
      </div>
      <div className={styles.summaryCard}>
        <ClockCircleOutlined className={styles.cardIcon} />
        <div className={styles.value}>{formatDurationFromSeconds(statistics.averageDurationSeconds)}</div>
        <div className={styles.label}>{t('quiz.statistics.avgDuration', 'Avg. Duration')}</div>
      </div>
    </div>
  );

  /**
   * Render the cumulative attempt trend line chart when trend data exists.
   *
   * @returns {JSX.Element|null} Trend chart section or null when no trend exists.
   */
  const renderAttemptTrend = () => {
    if (!statistics.attemptTrend || statistics.attemptTrend.length === 0) return null;

    const data = statistics.attemptTrend.map(item => ({
      date: item.date,
      count: item.cumulative,
    }));

    const config = {
      data,
      xField: 'date',
      yField: 'count',
      smooth: true,
      point: { size: 4 },
      color: '#1890ff',
      yAxis: { title: { text: t('quiz.statistics.totalAttempts', 'Total Attempts') } },
      xAxis: { title: { text: t('surveys.date', 'Date') } },
    };

    return (
      <div className={styles.chartsSection}>
        <h3 className={styles.sectionTitle}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          {t('quiz.statistics.attemptTrend', 'Attempt Trend')}
        </h3>
        <div className={styles.chartContainer}>
          <Line {...config} />
        </div>
      </div>
    );
  };

  /**
   * Render question-level performance cards for the current quiz collection.
   *
   * @returns {JSX.Element} Question analytics section.
   */
  const renderQuestionStats = () => {
    if (!statistics.questionStats || statistics.questionStats.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Empty description={t('quiz.statistics.noQuestionData', 'No question data yet')} />
        </div>
      );
    }

    return (
      <div className={styles.questionStatsSection}>
        <h3 className={styles.sectionTitle}>
          <QuestionCircleOutlined style={{ marginRight: 8 }} />
          {t('quiz.statistics.questionAccuracy', 'Per-Question Accuracy')}
        </h3>

        {[...statistics.questionStats]
          .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
          .map((qs, index) => {
          const accuracy = qs.accuracyPercent ?? 0;
          const color = accuracy >= 70 ? '#52c41a' : accuracy >= 40 ? '#faad14' : '#ff4d4f';
          const plainBody = qs.questionBody
            ? qs.questionBody.replace(/<[^>]*>/g, '').slice(0, 150)
            : '';

          return (
            <div key={qs.questionId ?? index} className={styles.questionCard}>
              <div className={styles.questionHeader}>
                <span className={styles.questionNumber}>
                  {qs.questionCode || `Q${index + 1}`}
                </span>
                <div className={styles.questionContent}>
                  <div className={styles.questionBodyRow}>
                    {qs.imageUrl && (
                      <div className={styles.questionImageWrap}>
                        <Image
                          src={getFileUrl(qs.imageUrl)}
                          alt=""
                          width={QUESTION_IMAGE_SIZE}
                          height={QUESTION_IMAGE_SIZE}
                          className={styles.questionImage}
                          preview={false}
                        />
                      </div>
                    )}
                    <span className={styles.questionBody}>
                      {plainBody}{plainBody.length >= 150 ? '…' : ''}
                      {!qs.questionId && (
                        <Tag color="red" style={{ marginLeft: 8 }}>
                          {t('quiz.statistics.deleted', 'Deleted')}
                        </Tag>
                      )}
                    </span>
                  </div>

                  {qs.choices?.length > 0 && (
                    <div className={styles.questionChoicesList}>
                      {qs.choices.map((choice) => (
                        <div
                          key={choice.id}
                          className={`${styles.questionChoiceItem} ${choice.isCorrect ? styles.questionChoiceItemCorrect : ''}`}
                        >
                          <span className={styles.choiceLeadingIcon}>
                            {choice.isCorrect ? <CheckCircleFilled /> : null}
                          </span>
                          {choice.imageUrl && (
                            <Image
                              src={getFileUrl(choice.imageUrl)}
                              alt=""
                              width={CHOICE_IMAGE_SIZE}
                              height={CHOICE_IMAGE_SIZE}
                              className={styles.choiceImage}
                              preview={false}
                            />
                          )}
                          <span className={styles.choiceText}>
                            {choice.text || t('quiz.statistics.imageChoice', '(Image)')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.questionStatsRow}>
                <div className={styles.questionStats}>
                  <span>
                    {t('quiz.statistics.answered', 'Answered')}: {qs.totalAnswered}
                  </span>
                  <span style={{ color: '#52c41a' }}>
                    {t('quiz.statistics.correct', 'Correct')}: {qs.correctCount}
                  </span>
                </div>
                <Button
                  icon={<BarChartOutlined />}
                  size="small"
                  className={styles.analyticsButton}
                  onClick={() => {
                    setAnalyticsQuestion(qs);
                    setAnalyticsModalOpen(true);
                  }}
                >
                  {t('quiz.questionAnalytics.viewDetails', 'View analytics')}
                </Button>
              </div>
              <Progress
                percent={Math.round(accuracy)}
                strokeColor={color}
                size="small"
                format={pct => `${pct}%`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.statisticsContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
            <Spin size="large" />
          </div>
        </div>
      </PagesWrapper>
    );
  }

  if (!statistics) return null;

  return (
    <PagesWrapper>
      <div className={styles.statisticsContainer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/quiz-collections')}
            >
              {t('common.back', 'Back')}
            </Button>
            <h1>
              {statistics.collectionName} - {t('quiz.statistics.title', 'Statistics')}
            </h1>
          </div>
          <div className={styles.headerActions}>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/quiz-collections/${id}`)}
            >
              {t('quiz.edit', 'Edit')}
            </Button>
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() => navigate(`/quiz-collections/${id}/attempts`)}
            >
              {t('quiz.statistics.viewAttempts', 'View Attempts')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchStatistics}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          </div>
        </div>

        {statistics.totalAttempts === 0 ? (
          <div className={styles.emptyState}>
            <Empty
              description={t('quiz.statistics.noAttempts', 'No quiz attempts recorded yet. Share the quiz to start collecting data.')}
            />
          </div>
        ) : (
          <>
            {renderSummaryCards()}
            {renderAttemptTrend()}
            {renderQuestionStats()}
            <QuestionAnalyticsModal
              open={analyticsModalOpen}
              collectionId={parseInt(id, 10)}
              question={analyticsQuestion}
              onClose={() => {
                setAnalyticsModalOpen(false);
                setAnalyticsQuestion(null);
              }}
            />
          </>
        )}
      </div>
    </PagesWrapper>
  );
};

export default QuizStatistics;
